// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useMemo } from 'react';
import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { type HistoricalBalances } from '@shared/data-access/types/brokerage-types';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import { aggregateHistoricalBalances } from '../utils/aggregate-historical-balances';

export interface UseHistoricalBalancesArgs {
  clientId: number | undefined;
  start_date: string;
  end_date: string;
  ibans: string[];
  institutionId: string;
  skip?: boolean;
  currency: AccountCurrency;
}

export const useHistoricalBalances = ({
  clientId,
  end_date,
  ibans,
  start_date,
  institutionId,
  skip,
  currency,
}: UseHistoricalBalancesArgs) => {
  const accountsBalancesHistoricalQuery =
    advisoryApi.useGetAdvisoryAccountsBalancesHistoricalQuery(
      {
        client_id: clientId,
        start_date: start_date,
        end_date: end_date,
        institution_id: institutionId,
        currency,
      },
      { skip: skip },
    );

  const historicalBalancesData = useMemo(() => {
    const data = accountsBalancesHistoricalQuery.data ?? [];
    return data.filter(account => ibans.includes(account.iban));
  }, [accountsBalancesHistoricalQuery.data, ibans]);

  const institutionsHistoricalBalances = useMemo(() => {
    return historicalBalancesData.map(account => {
      const equity = account.historical_balances.net_value.reduce(
        (acc, curr) => acc + curr,
        0,
      );
      const debt = account.historical_balances.debt.reduce(
        (acc, curr) => acc + curr,
        0,
      );
      const investment = account.historical_balances.investment.reduce(
        (acc, curr) => acc + curr,
        0,
      );
      const cash = account.historical_balances.cash.reduce(
        (acc, curr) => acc + curr,
        0,
      );
      const net_value = account.historical_balances.net_value.reduce(
        (acc, curr) => acc + curr,
        0,
      );

      return {
        iban: account.iban,
        institution_id: account.institution_id,
        equity: equity,
        debt: debt,
        investment: investment,
        cash: cash,
        net_value: net_value,
      };
    });
  }, [historicalBalancesData]);

  const historicalBalances: HistoricalBalances = useMemo(() => {
    const balances = historicalBalancesData.map(
      account => account.historical_balances,
    );
    return aggregateHistoricalBalances(balances);
  }, [historicalBalancesData]);

  const hasHistoricalBalances = useMemo(
    () =>
      [
        historicalBalances.net_value.length,
        historicalBalances.dates.length,
        historicalBalances.investment.length,
        historicalBalances.debt.length,
        historicalBalances.cash.length,
      ].some(Boolean),
    [historicalBalances],
  );

  return {
    institutionsHistoricalBalances,
    historicalBalances,
    hasHistoricalBalances,
    accountsBalancesHistoricalQuery,
  };
};
